import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import useStudySession from 'hooks/useStudySession';
import { setError } from 'store/error';
import Loader from 'components/Loader';
import Module from 'components/SATModule';

import './index.css';

function Assignment() {
    const [isLoading, setIsLoading] = useState(true);
    const [name, setName] = useState('');
    const [testType, setTestType] = useState('');
    const [subject, setSubject] = useState('');
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [questionTime, setQuestionTime] = useState(0);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { assignmentId } = useParams();

    useStudySession({
        activityType: 'assignment',
        activityIds: { studentQuestionSetId: assignmentId },
    });

    const autoSave = () => {
        localStorage.setItem('assignment', JSON.stringify({
            assignmentId: assignmentId,
            answers: answers,
        }));
    }

    const navigateQuestion = (index: number) => {
        if (questionIndex >= 0 && questionIndex < questions.length) {
            let newAnswers = [...answers];
            newAnswers[questionIndex].timeSpent += questionTime;
            setAnswers(newAnswers);
        }
        setQuestionTime(0);
        setQuestionIndex(index);

        autoSave();
    }

    const setAnswer = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answer;
        setAnswers(newAnswers);

        autoSave();
    }

    const setChoice = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answers[questionIndex].answer == answer
            ? ''
            : answer;

        newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== answer);
        setAnswers(newAnswers);

        autoSave();
    }

    const markQuestion = () => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].marked = !answers[questionIndex].marked;
        setAnswers(newAnswers);

        autoSave();
    }

    const crossOutChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionIndex].crossedOut.includes(choiceId)) {
            newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== choiceId);
        }
        else {
            newAnswers[questionIndex].crossedOut.push(choiceId);
        }
        if (choiceId === answers[questionIndex].answer) {
            newAnswers[questionIndex].answer = '';
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations[index] = annotation;
        }
        else {
            const coveredAnnotations = answers[questionIndex].annotations.filter(existing => (
                existing.passageId === annotation.passageId &&
                existing.startIndex >= annotation.startIndex &&
                existing.endIndex <= annotation.endIndex
            ));

            newAnswers[questionIndex].annotations.push(annotation);

            for (let i = 0; i < coveredAnnotations.length; i++) {
                const coveredIndex = newAnswers[questionIndex].annotations.findIndex(existing => (
                    existing.passageId === coveredAnnotations[i].passageId &&
                    existing.startIndex === coveredAnnotations[i].startIndex &&
                    existing.endIndex === coveredAnnotations[i].endIndex
                ));

                const coveredAnnotation = newAnswers[questionIndex].annotations.splice(coveredIndex, 1)[0];
                newAnswers[questionIndex].annotations.push(coveredAnnotation);
            }
        }
        setAnswers(newAnswers);
        autoSave();
    }

    const deleteAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations.splice(index, 1);
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const handleSubmit = () => {
        axios.post(`questions/assignment/submit/${assignmentId}/`, {
                answers: answers
            })
            .then(() => {
                navigate('/assignments');
            })
            .catch(error => {
                console.log(error);
                dispatch(setError(error));
            });
    }

    const handleSaveAndExit = () => {
        axios.post(`questions/assignment/save/${assignmentId}/`, {
                answers: answers
            })
            .then(() => {
                navigate(-1);
            })
            .catch(error => {
                console.log(error);
                dispatch(setError(error));
            });
    }
    
    const handleDiscardAndExit = () => {
        localStorage.removeItem('assignment');
        navigate(-1);
    }

    useEffect(() => {
        axios.get(`questions/assignment/${assignmentId}/`)
            .then(res => {
                // console.log(res.data);
                setName(res.data.name);
                setTestType(res.data.testType);
                setSubject(res.data.subject);
                setQuestions(res.data.questions);

                if (localStorage.getItem('assignment')) {
                    const autosave = JSON.parse(localStorage.getItem('assignment'));
                    if (autosave.assignmentId === assignmentId) {
                        setAnswers(autosave.answers);
                    }
                    else {
                        setAnswers(res.data.answers);
                    }
                }
                else {
                    setAnswers(res.data.answers);
                }

                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (questionTime === null) return;

        const questionTimer = setInterval(() => {
            setQuestionTime(questionTime + 1);
        }, 1000);

        return () => clearInterval(questionTimer);
    }, [questionTime]);

    if (isLoading) return <Loader />;

    return (
        <div className='assignment'>
            <div className='assignment__box'>
                <Module
                    moduleName={name}
                    subject={subject}
                    questions={questions}
                    questionIndex={questionIndex}
                    answers={answers}
                    markQuestion={markQuestion}
                    crossOutChoice={crossOutChoice}
                    saveAnnotation={saveAnnotation}
                    deleteAnnotation={deleteAnnotation}
                    handleChange={value => setAnswer(value)}
                    handleSelect={value => setChoice(value)}
                    navigateQuestion={navigateQuestion}
                    handleSaveAndExit={handleSaveAndExit}
                    handleDiscardAndExit={handleDiscardAndExit}
                    handleSubmit={handleSubmit}
                />
            </div>
        </div>
    );
}

export default Assignment;